import { ConfirmProvider } from "material-ui-confirm";
import { SnackbarProvider } from "notistack";
import { Switch, Route, Redirect, BrowserRouter } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";

import { makeStyles, ThemeProvider, CssBaseline } from "@material-ui/core";

import Auth0ApolloProvider from "src/components/Auth0ApolloProvider";
import AuthProvider, {
  useNonStaffAuth0,
  useStaffAuth0
} from "src/components/AuthProvider";
import LoadingIndicator from "src/components/LoadingIndicator";
import Login from "src/pages/Login";
import palette from "src/styles/palette";
import theme from "src/styles/theme";

import AppRouter from "./Routes";
import CaptchaProvider from "./components/CaptchaProvider";
import useBuildTimestampRefresh from "./util/useBuildTimestampRefresh";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    height: "100%",
    overflow: "hidden",
    width: "100%"
  },
  pageWrapper: {
    flex: "1"
  },
  notificationInfo: {
    backgroundColor: palette.navy
  },
  snackbarWithHelpWidget: {
    marginBottom: "80px"
  }
}));

function AuthorizedRoute(props: any) {
  const staffAuth0 = useStaffAuth0();
  const nonStaffAuth0 = useNonStaffAuth0();
  const classes = useStyles();

  if (staffAuth0.isLoading || nonStaffAuth0.isLoading) {
    return (
      <div className={classes.pageWrapper}>
        <LoadingIndicator />
      </div>
    );
  }
  if (!staffAuth0.isAuthenticated && !nonStaffAuth0.isAuthenticated) {
    return (
      <Redirect
        push
        to={`/login?returnTo=${encodeURIComponent(
          document.location.pathname + document.location.search
        )}`}
      />
    );
  }
  return <Route {...props} />;
}

function AppWithRefresher(): JSX.Element {
  useBuildTimestampRefresh();

  const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID;

  if (!auth0Domain || !auth0ClientId) {
    throw new Error("Misconfigured: missing Auth0 config");
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <Auth0ApolloProvider>
          <QueryParamProvider ReactRouterRoute={Route}>
            <Switch>
              <Route path="/login" component={Login} />
              <AuthorizedRoute component={AppRouter} />
            </Switch>
          </QueryParamProvider>
        </Auth0ApolloProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function App(): JSX.Element {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <ThemeProvider theme={theme}>
        <SnackbarProvider
          maxSnack={3}
          classes={{
            containerRoot: window.FreshworksWidget
              ? classes.snackbarWithHelpWidget
              : undefined,
            variantInfo: classes.notificationInfo
          }}
        >
          <ConfirmProvider
            defaultOptions={{
              // This auto-focuses the "confirm" button so you can confirm by
              // pressing Enter
              confirmationButtonProps: {
                autoFocus: true,
                variant: "contained",
                color: "primary"
              }
            }}
          >
            <CaptchaProvider>
              <CssBaseline />
              <AppWithRefresher />
            </CaptchaProvider>
          </ConfirmProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
